import { computed, type ComputedRef, type Ref, ref } from "vue";
import {
    type EventObject,
    type ParsedData,
    UrlParser,
    type UrlParsersAndTypesKey,
} from "../seo-url-parser";
import {
    flattenAllCollections,
    flattenAllCollectionsWithParents,
    getCollectionLevel,
    iterateUp,
} from "./collection-methods";
import { type MappedProductCollectionModel } from "./models";
import {
    collectionIdKeyword,
    collectionIdSeparator,
    collectionIdSetter,
} from "./url";
import { makeGroupedValue } from "./value";
import {
    type PreselectedProductCollectionGroup,
    type ProductCollectionLeveledStructure,
} from "../types";
import { getProductCollectionTree as webApiGetProductCollectionTree } from "./web-api";
import { constructMappedItem } from "./view-modes/mapped-items";

interface StateObject {
    preselectedCollectionIds: Ref<number[][] | undefined>;
    root: Ref<MappedProductCollectionModel[] | undefined>;
    currentStructure: Ref<ProductCollectionLeveledStructure | undefined>;
    includeTest: Ref<boolean>;
    productListGuid: Ref<number | undefined>;
    actualViewMode: Ref<number>;
    getProductCollectionTree: Ref<
        typeof webApiGetProductCollectionTree | undefined
    >;
    manualScopedRoot: Ref<MappedProductCollectionModel | undefined>;
    selectedScopedRoot: Ref<MappedProductCollectionModel | undefined>;
    overwrittenVisibilities: Ref<number[]>;
    mappedFlatRoot: ComputedRef<MappedProductCollectionModel[]>;
    lastRequestedData: Ref<
        Parameters<typeof webApiGetProductCollectionTree> | undefined
    >;
}

export function createUrlParserWrapper() {
    const state: StateObject = {
        preselectedCollectionIds: ref(),
        root: ref(),
        currentStructure: ref(),
        includeTest: ref(false),
        productListGuid: ref(),
        actualViewMode: ref(0),
        getProductCollectionTree: ref(),
        manualScopedRoot: ref(),
        selectedScopedRoot: ref(),
        overwrittenVisibilities: ref([]),
        mappedFlatRoot: null!,
        lastRequestedData: ref(),
    };
    state.mappedFlatRoot = computed(() => {
        return Array.from(flattenAllCollections(state.root.value));
    });
    (async function () {
        const { collections, currentStructure: currentStruct } =
            await getGroupList(state);

        state.root.value = collections.map((x) =>
            constructMappedItem(x, undefined)
        );
    });

    const postFormChangeHandlers: PostFormChangeHandler[] = [];

    const collectionParser = createUrlParser({
        getRoot: () => state.root.value,
        getViewMode: () => state.actualViewMode.value,
        overwriteVisibility: (col) => overwriteVisibility(state, col),
        setScopedRoot: (col) => (state.manualScopedRoot.value = col),
        postFormChange: async (colId, scopedRootId) => {
            state.selectedScopedRoot.value = state.mappedFlatRoot.value.find(
                (x) => x.Id == scopedRootId
            );
            for (const handler of postFormChangeHandlers) {
                await handler(colId, scopedRootId);
            }
        },
    });

    return {
        state,
        postFormChangeHandlers,
        parser: collectionParser,
    };
}

function overwriteVisibility(
    state: StateObject,
    item: { Visible: boolean; Id: number }
) {
    if (item.Visible !== true) {
        if (state.overwrittenVisibilities.value.indexOf(item.Id) === -1)
            state.overwrittenVisibilities.value.push(item.Id);
    }
}

function constructCollectionIdGroupModelItems(
    state: StateObject,
    collIdGroups: number[][] | undefined | null
): PreselectedProductCollectionGroup[] {
    if (collIdGroups == null) return [];
    const result: PreselectedProductCollectionGroup[] = [];

    const mappedWithParents = Array.from(
        flattenAllCollectionsWithParents(state.root.value)
    );

    for (const collectionIdGroup of collIdGroups) {
        const group: PreselectedProductCollectionGroup = {
            ParentCollectionGroupId: 0,
            ProductCollectionGroups: [],
        };
        for (const collectionId of collectionIdGroup) {
            if (group.ParentCollectionGroupId == null) {
                const match = mappedWithParents.find(
                    (set) => set.collection.Id == collectionId
                );
                if (match != null) {
                    group.ParentCollectionGroupId = match.parents[0].Id;
                }
            }
            group.ProductCollectionGroups.push(collectionId);
        }
        if (
            group.ParentCollectionGroupId != null &&
            group.ProductCollectionGroups.length > 0
        ) {
            result.push(group);
        }
    }
    return result;
}

async function getGroupList(state: StateObject) {
    const args: Parameters<typeof webApiGetProductCollectionTree> = [
        state.productListGuid.value,
        constructCollectionIdGroupModelItems(
            state,
            state.preselectedCollectionIds.value
        ),
        state.currentStructure.value,
        state.includeTest.value,
    ];
    const result = await (
        state.getProductCollectionTree.value ?? webApiGetProductCollectionTree
    )(...args);
    state.lastRequestedData.value = args;
    return result;
}

export interface UrlParserData {
    collection: MappedProductCollectionModel;
    isRootMatch: boolean;
    level: number;
}

export function findCollectionByLogicalName(
    logicalName: string,
    collections: MappedProductCollectionModel[]
): MappedProductCollectionModel | undefined {
    for (const item of collections) {
        if (typeof item.LogicalName === "string") {
            if (item.LogicalName.toLowerCase() === logicalName.toLowerCase())
                return item;
        }

        if ("SubCollections" in item && item.SubCollections != null) {
            const bySubItems = findCollectionByLogicalName(
                logicalName,
                item.SubCollections
            );
            if (bySubItems != null) return bySubItems;
        }
    }
    return undefined;
}

export function findCollectionById(
    id: number,
    collections: MappedProductCollectionModel[]
): MappedProductCollectionModel | undefined {
    for (const item of collections) {
        if (typeof item.Id === "number") {
            if (item.Id === id) return item;
        }

        if ("SubCollections" in item && item.SubCollections != null) {
            const bySubItems = findCollectionById(id, item.SubCollections);
            if (bySubItems != null) return bySubItems;
        }
    }
    return undefined;
}

export type PostFormChangeHandler = (
    collectionId: number[][],
    scopedRootId: number | undefined
) => Promise<void> | Promise<unknown>;

export function createUrlParser({
    getRoot,
    getViewMode,
    overwriteVisibility,
    setScopedRoot,
    postFormChange,
}: {
    getRoot: () => MappedProductCollectionModel[] | null | undefined;
    getViewMode: () => number;
    overwriteVisibility: (collection: any) => void;
    setScopedRoot: (collection: MappedProductCollectionModel) => void;
    postFormChange: PostFormChangeHandler;
}): UrlParser<UrlParsersAndTypesKey> {
    return new UrlParser<"product-collection">(
        "product-collection",
        function* ({
            part,
            parsedDatas,
            matchedLogicalName,
            matchedLogicalNameModuleGuid,
            matchedLogicalNameSubModuleGuid,
            matchedLogicalNameEntityGuid,
        }) {
            console.debug(
                "product-collection-parser",
                part,
                parsedDatas,
                matchedLogicalName,
                matchedLogicalNameModuleGuid,
                matchedLogicalNameSubModuleGuid,
                matchedLogicalNameEntityGuid
            );

            const root = getRoot();

            if (root == null) throw new Error("missing root content");

            if (
                matchedLogicalNameModuleGuid === 392 &&
                matchedLogicalNameSubModuleGuid === 0
            ) {
                // This pageblock is actually a product collection. This mean that matchedLogicalName is a logicalName of a product collection.
                // Because of that, we have to resolve the first part too. If it was an article, the matchedLogicalName would not be relevant in this function.
                const rootMatch = findCollectionByLogicalName(
                    matchedLogicalName,
                    root
                );

                if (rootMatch == null)
                    throw new Error(
                        "Could not find match by root logical name"
                    );

                yield {
                    collection: rootMatch,
                    isRootMatch: true,
                    level: getCollectionLevel(rootMatch, root),
                };
            }

            const itemMatch =
                (parsedDatas[parsedDatas.length - 1] != null
                    ? findCollectionByLogicalName(
                          part,
                          parsedDatas[parsedDatas.length - 1].data.collection
                              .SubCollections
                      )
                    : undefined) ?? findCollectionByLogicalName(part, root);
            if (itemMatch != null) {
                yield {
                    collection: itemMatch,
                    isRootMatch: false,
                    level: getCollectionLevel(itemMatch, root),
                };
            } else {
                const prefix = collectionIdKeyword + collectionIdSetter;
                if (part.toLowerCase().indexOf(prefix.toLowerCase()) !== -1) {
                    const collectionIds = part
                        .substring(1)
                        .split(collectionIdSeparator);
                    for (const collectionIdStr of collectionIds) {
                        const collectionId = parseInt(
                            collectionIdStr.substring(prefix.length)
                        );
                        const collection = findCollectionById(
                            collectionId,
                            root
                        );
                        if (collection != null)
                            yield {
                                collection: collection,
                                isRootMatch: false,
                                level: getCollectionLevel(collection, root),
                            };
                    }
                }
            }

            //let groups;
            //let group;

            //if (parsedDatas.length === 0) {
            //    groups = comp.groups;
            //    group = null;
            //} else {
            //    var latestData = parsedDatas[parsedDatas.length - 1];
            //    if (latestData.type != "product-group") {
            //        console.debug("Cannot parsed product group when latest part type is parsed as "+latestData.type);
            //        return null;
            //    }
            //    groups = latestData.data.groups;
            //    group = latestData.data.group;
            //}
            //var matches = groups.filter(x => x.subLogicalName.toLowerCase() === part);
            //if (matches.length > 0) {
            //    console.debug("Did find match on part: "+part);
            //    var parsedGroup = matches[0];
            //    return {group: parsedGroup, groups: parsedGroup.children, parent: group };
            //}
            //console.debug("Did not find match on part: "+part);
            //return null;
            return undefined;
        },
        (event) => {
            event.register({
                name: "applyAll",
                handler: async (
                    eventObj: EventObject,
                    parsedDatas: ParsedData<"product-collection">[]
                ) => {
                    console.debug("APPLYALLE", eventObj, parsedDatas);
                    const viewMode = getViewMode();
                    let lastViewMode: number | undefined;
                    let result: number[][] = [];
                    let scopedId: number | undefined = undefined;

                    const highestLevel = parsedDatas.reduce((prev, curr) => {
                        if (curr.data.level > prev) return curr.data.level;
                        return prev;
                    }, -1);

                    for (const parsedData of parsedDatas) {
                        if (parsedData.type !== "product-collection") continue;
                        const parsedDataContent: UrlParserData =
                            parsedData.data;
                        const parsedCollection = parsedDataContent.collection;
                        if (parsedCollection.Visible !== true) {
                            overwriteVisibility(parsedCollection);
                        }
                        const tempViewMode =
                            parsedData.data.collection.parent?.parent
                                ?.FilterViewMode ??
                            parsedData.data.collection.parent?.parent?.parent
                                ?.FilterViewMode ??
                            parsedData.data.collection.parent?.parent?.parent
                                ?.parent?.FilterViewMode;
                        if (tempViewMode != null) lastViewMode = tempViewMode;
                        const actualViewMode = tempViewMode ?? viewMode;
                        if (
                            actualViewMode == 0 &&
                            parsedDataContent.isRootMatch === true
                        ) {
                            setScopedRoot(parsedCollection);
                        }

                        if (actualViewMode == 1) {
                            if (parsedDataContent.level === 0) {
                                continue;
                            }
                            if (
                                parsedData.data.level < highestLevel &&
                                parsedDatas.some(
                                    (x) =>
                                        x.data.collection.Id !=
                                            parsedData.data.collection.Id &&
                                        Array.from(
                                            iterateUp(x.data.collection)
                                        ).some(
                                            (y) =>
                                                y.Id ==
                                                parsedData.data.collection.Id
                                        )
                                )
                            ) {
                                continue;
                            }
                            const root = getRoot();
                            const {
                                value: tempResult,
                                scopedRootId: tempScopedRootId,
                            } = makeGroupedValue(
                                parsedDataContent.collection.Id,
                                result,
                                actualViewMode,
                                root
                            );

                            result = tempResult;
                            scopedId = tempScopedRootId;

                            console.log(
                                `result is now1 ${JSON.stringify(
                                    result
                                )} with scopedId:${scopedId}`
                            );
                        }
                    }
                    const lastItem = parsedDatas[parsedDatas.length - 1];
                    const lastItemData: UrlParserData = lastItem.data;
                    const collection = lastItemData.collection;
                    const collectionId = collection.Id;
                    const { value: tmpresult, scopedRootId: tempScopedRootId } =
                        makeGroupedValue(
                            collectionId,
                            result,
                            lastViewMode ?? viewMode,
                            getRoot()
                        );
                    scopedId = tempScopedRootId;
                    result = tmpresult;
                    console.log(
                        `result is now2 ${JSON.stringify(
                            result
                        )} with scopedId:${scopedId}`
                    );
                    await postFormChange(result, scopedId);
                },
            });
        }
    );
}
